import { Box } from '@material-ui/core'
import { resolveRemindersPausedReason } from '@shared/core/billing'
import { GetProfileResponseDto } from '@shared/core/users'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import generateRandomId from 'src/helpers/generate-random-id'
import { getDayOfWeek } from 'src/helpers/get-day-of-week'
import { getEventDate } from 'src/helpers/get-event-date'
import { Button, Icon, PhoneNumberField } from 'src/modules/core/components'
import { DateContext } from 'src/modules/core/context'
import { getDefaultCountryCode, getPhoneAndCountryCode } from 'src/modules/core/utils/phone-input.utils'
import { useDismissSuggestions } from 'src/modules/phone-suggestions/hooks/use-phone-suggestions'
import { useUserPreferences } from 'src/modules/preferences/hooks/use-user-preferences'
import { ReviewTimingIssuesModal } from 'src/modules/reminder/components/review-timing-issues-modal'
import { ReviewTimingIssuesLogic } from 'src/modules/reminder/components/review-timing-issues-modal/review-timing-issues-logic'
import { useValidatePhoneNumbers } from 'src/modules/sms/sms-compliance/hooks/use-sms-compliance'
import { eventPreviewFields } from '../../constants/event-params'
import { useStandingConsentAcceptanceHints } from '../../hooks/use-standing-consent-acceptance-hints'
import { useUpdateEvent } from '../../hooks/use-update-event'
import { EventPreview } from '../../types/event.types'
import { EventAutoActivationHelper } from '../../utils/EventAutoActivationHelper'
import { EventSuggestedNumbersHelper } from '../../utils/EventSuggestedNumbersHelper'
import { setEventRecentlyActivated } from '../../utils/recently-activated-events'
import { EventCardBulkAutoActivateSwitch } from './event-card-bulk-auto-activate-switch'
import { EditEventForm } from './event-card'
import { EventCardPausedActions } from './event-card-paused-actions'
import classes from './event-card-phone-fields.module.scss'

export type EventCardPhoneFieldsProps = {
  profile: GetProfileResponseDto
  event: EventPreview
  hasFocus?: boolean
  onPhoneNumbersChange?: (receiverNumbers: any[]) => void
}

export function EventCardPhoneFields({ profile, event, hasFocus, onPhoneNumbersChange }: EventCardPhoneFieldsProps) {
  const defaultCountryCode = getDefaultCountryCode(profile)
  const remindersPausedReason = resolveRemindersPausedReason(profile)
  const eventDate = useMemo(() => getEventDate(event), [event])
  const isPastEvent = useMemo(() => eventDate < new Date(), [eventDate])
  const { data: userPreferences } = useUserPreferences()

  const [bulkAutoActivate, setBulkAutoActivate] = useState(false)
  const [bulkAutoActivateTouched, setBulkAutoActivateTouched] = useState(false)

  // Add a forceUpdate state to trigger re-render when needed
  const [, forceUpdate] = useState({})

  // State to force re-render of phone number fields
  const [phoneNumberKey, setPhoneNumberKey] = useState(0)
  const dismissSuggestions = useDismissSuggestions()

  // Phone validation state
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<Record<number, string>>({})
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [phoneNumbersToValidate, setPhoneNumbersToValidate] = useState<string[]>([])

  const { startDate, endDate } = useContext(DateContext)
  const params = useMemo(
    () => ({
      fields: eventPreviewFields,
      start_date: startDate,
      end_date: endDate,
    }),
    [startDate, endDate],
  )
  const updateEvent = useUpdateEvent(params)

  const eventPhoneNumbers = Array.from(
    new Set([...(event?.receiver_numbers ?? []), ...EventSuggestedNumbersHelper.getPendingSuggestions(event)]),
  ).filter(Boolean)
  const defaultReceiverNumbers = useMemo(
    () => (profile.usage_mode === 'personal' ? profile.phone_numbers : (eventPhoneNumbers ?? [])),
    [profile.usage_mode, profile.phone_numbers, eventPhoneNumbers],
  )
  if (defaultReceiverNumbers.length === 0) {
    defaultReceiverNumbers.push('')
  }

  const {
    control,
    watch,
    setValue,
    formState: { isValid, isDirty, errors },
    handleSubmit: onSubmit,
    setError,
    clearErrors,
  } = useForm<EditEventForm>({
    defaultValues: {
      dummy: '',
      receiverNumbers: defaultReceiverNumbers.map((n) => ({
        id: generateRandomId(),
        ...getPhoneAndCountryCode(n, defaultCountryCode),
      })),
    },
  })

  const {
    fields: phones,
    append: addPhone,
    remove: removePhone,
  } = useFieldArray({
    control,
    name: 'receiverNumbers',
  })

  // Watch phone number fields for validation
  const watchedPhoneNumbers = watch('receiverNumbers')

  const standingConsentCandidates = useMemo(() => {
    const phones = watchedPhoneNumbers?.map((entry) => entry.phone).filter(Boolean) ?? []
    return EventAutoActivationHelper.getStandingConsentCandidates(
      event,
      phones,
      Boolean(userPreferences?.user_preferences?.auto_activate_when_clear),
      isPastEvent,
    )
  }, [userPreferences?.user_preferences?.auto_activate_when_clear, event, isPastEvent, watchedPhoneNumbers])

  const { data: standingConsentHints } = useStandingConsentAcceptanceHints(
    standingConsentCandidates,
    standingConsentCandidates.length > 0,
  )

  useEffect(() => {
    setBulkAutoActivate(false)
    setBulkAutoActivateTouched(false)
  }, [event.id])

  // Extract phone numbers for validation
  const phoneNumbersForValidation = useMemo(() => {
    return (
      watchedPhoneNumbers?.map((n) => n.phone).filter((phone) => phone && phone.trim() !== '' && phone !== '+') || []
    )
  }, [watchedPhoneNumbers])

  // Debounced validation effect
  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }

    if (phoneNumbersForValidation.length === 0) {
      setPhoneValidationErrors({})
      setPhoneNumbersToValidate([])
      return
    }

    validationTimeoutRef.current = setTimeout(() => {
      setPhoneNumbersToValidate(phoneNumbersForValidation)
    }, 600)

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [phoneNumbersForValidation])

  // Use the validation hook
  const { data: validationResults } = useValidatePhoneNumbers(phoneNumbersToValidate, {
    enabled: phoneNumbersToValidate.length > 0 && hasFocus,
  })

  // Process validation results and map to field indices
  useEffect(() => {
    if (!validationResults || !watchedPhoneNumbers) {
      return
    }

    const newErrors: Record<number, string> = {}

    validationResults.forEach((result) => {
      if (!result.isValid && result.invalidReason) {
        const fieldIndex = watchedPhoneNumbers.findIndex((field) => {
          const fieldPhone = field.phone || ''
          const resultPhone = result.phoneNumber || ''
          return fieldPhone === resultPhone
        })

        if (fieldIndex !== -1) {
          newErrors[fieldIndex] = result.errorMessage
        }
      }
    })

    setPhoneValidationErrors(newErrors)
  }, [validationResults, watchedPhoneNumbers])

  function isSuggested(phone: string) {
    return EventSuggestedNumbersHelper.isSuggestedNumber(event, phone)
  }

  function hasSuggested() {
    return phones.some((p) => p.phone && EventSuggestedNumbersHelper.isSuggestedNumber(event, p.phone))
  }

  function handleAddPhone() {
    addPhone({
      id: generateRandomId(),
      phone: '',
      country: defaultCountryCode,
    })
  }

  function handleRemovePhone(index: number) {
    // Get current form values to avoid stale data
    const currentReceiverNumbers = watch('receiverNumbers')
    const phoneToRemove = currentReceiverNumbers[index]

    // Check if the phone number being removed is a suggested number
    if (phoneToRemove?.phone && isSuggested(phoneToRemove.phone)) {
      // Call dismiss suggestions mutation for suggested numbers
      dismissSuggestions.mutate({
        eventId: event.id,
        title: event.title || '',
        dayOfWeek: getDayOfWeek(eventDate),
        removedNumbers: [phoneToRemove.phone],
        clientTimestamp: new Date().toISOString(),
      })
    }

    if (index === 0 && currentReceiverNumbers.length === 1) {
      // If it's the first and only phone number, clear it instead of removing
      const currentPhone = currentReceiverNumbers[index]
      setValue(
        `receiverNumbers.${index}`,
        {
          ...currentPhone,
          phone: '',
          country: defaultCountryCode,
        },
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      )
      // Force re-render of phone number fields
      setPhoneNumberKey((prev) => prev + 1)
      // Clear validation error for this field
      setPhoneValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
    } else {
      // Otherwise, remove the phone number normally
      removePhone(index)
      // Clear validation error for this field
      setPhoneValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
    }
  }

  function handleStackClick(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation()
  }

  const reviewTimingIssuesRef = useRef<'closed' | 'open' | 'send'>('closed')

  const handleSubmit = useCallback(
    (data: EditEventForm) => {
      if (isPastEvent) {
        setError('dummy', { type: 'manual', message: 'Event is in the past' })
        return
      }

      // Check for phone validation errors before submission
      if (Object.keys(phoneValidationErrors).length > 0) {
        return
      }

      const template = profile.default_message_template
      const hasConfirmation = template.confirmation_enabled && template.confirmation_offsets?.length > 0
      const hasReminder = template.reminder_enabled && template.reminder_offsets?.length > 0
      const hasFollowUp = template.follow_up_enabled && template.follow_up_offsets?.length > 0
      if (!hasConfirmation && !hasReminder && !hasFollowUp) {
        setError('dummy', { type: 'manual', message: 'Must have at least one message type enabled' })
        return
      }

      const result = ReviewTimingIssuesLogic.evaluate({
        confirmation_enabled: event.confirmation_enabled,
        confirmation_offsets: event.confirmation_offsets,
        reminder_enabled: event.reminder_enabled,
        reminder_offsets: event.reminder_offsets,
        event_date: eventDate,
      })
      if (reviewTimingIssuesRef.current !== 'send') {
        if (!result.allClear) {
          reviewTimingIssuesRef.current = 'open'
          return
        }
      }

      let send_reminder_immediately = false
      if (reviewTimingIssuesRef.current === 'send') {
        send_reminder_immediately = result.offerImmediateConfirmation || result.offerImmediateReminder
        reviewTimingIssuesRef.current = 'closed'
      }

      if (!isValid) {
        return
      }

      const standingConsentPayload =
        bulkAutoActivate && standingConsentCandidates.length > 0
          ? { grant_standing_consent_for: standingConsentCandidates }
          : {}

      setEventRecentlyActivated(event.id)
      updateEvent.mutate({
        id: event.id,
        active: true,
        send_reminder_immediately,
        receiver_numbers: data.receiverNumbers.map((p) => p.phone.trim()).filter((p) => p && p !== '+'),
        ...standingConsentPayload,
      })
    },
    [
      updateEvent,
      eventDate,
      isPastEvent,
      isValid,
      event.id,
      event.confirmation_enabled,
      event.confirmation_offsets,
      event.reminder_enabled,
      event.reminder_offsets,
      profile.default_message_template,
      setError,
      phoneValidationErrors,
      bulkAutoActivate,
      standingConsentCandidates,
    ],
  )

  useEffect(() => {
    if (reviewTimingIssuesRef.current === 'send') {
      onSubmit(handleSubmit)()
    }
  }, [reviewTimingIssuesRef, handleSubmit, onSubmit])

  const [showErrors, setShowErrors] = useState(false)
  const showErrorsTimeoutRef = useRef(null)
  useEffect(() => () => clearTimeout(showErrorsTimeoutRef.current), [])

  // Show errors immediately when component gains focus and has validation errors
  useEffect(() => {
    if (hasFocus && Object.keys(phoneValidationErrors).length > 0) {
      setShowErrors(true)
    }
  }, [hasFocus, phoneValidationErrors])

  function handlePhoneInputChange() {
    clearTimeout(showErrorsTimeoutRef.current)
    setShowErrors(false)
    showErrorsTimeoutRef.current = setTimeout(() => setShowErrors(true), 800)
  }

  function hasErrors() {
    return !isValid || Object.keys(errors).length > 0 || Object.keys(phoneValidationErrors).length > 0
  }

  function getErrorMessage() {
    // Show phone validation errors first (only the first one)
    if (Object.keys(phoneValidationErrors).length > 0) {
      return Object.values(phoneValidationErrors)[0]
    }

    // Then show form validation errors
    if (!Object.keys(errors).length) return null
    if (errors.receiverNumbers) {
      const messages = (errors.receiverNumbers as any)
        .filter((e: any) => e?.phone?.message)
        .map((e: any) => e.phone.message)
      return Array.from(new Set(messages)).join(', ')
    }
    if (errors.dummy) return errors.dummy.message
  }

  // Watch for changes to receiver numbers
  useEffect(() => {
    const subscription = watch((value) => {
      if (onPhoneNumbersChange) {
        onPhoneNumbersChange(value.receiverNumbers)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, onPhoneNumbersChange])

  if (profile.usage_mode !== 'customer') {
    return null
  }

  return (
    <>
      <div onClick={handleStackClick}>
        <form onSubmit={onSubmit(handleSubmit)} className={classes.stack}>
          <span className={classes.title}>Recipent phone number</span>
          {hasSuggested() && <span className={classes.suggestedTitle}>Suggested numbers in pink</span>}
          <div className={classes.phoneInputStack}>
            {phones.map((phone, index) => (
              <div className={classes.phoneInputContainer} key={phone.id}>
                <Controller
                  control={control}
                  name={`receiverNumbers.${index}`}
                  render={(props) => (
                    <PhoneNumberField
                      key={`${phone.id}-${phoneNumberKey}`}
                      value={props.field.value?.phone || ''}
                      defaultCountry={defaultCountryCode}
                      error={
                        isDirty && errors.receiverNumbers && errors.receiverNumbers[index]?.phone.message
                          ? errors.receiverNumbers[index]?.phone.message
                          : phoneValidationErrors[index]
                      }
                      onChange={(value: string, countryCode: string | null) => {
                        handlePhoneInputChange()
                        props.field.onChange({
                          country: countryCode,
                          phone: `+${value.replace(/\D/g, '')}`,
                        })

                        // Trigger phone validation after field update
                        setTimeout(() => {
                          const currentReceiverNumbers = watch('receiverNumbers')
                          const phoneNumbers =
                            currentReceiverNumbers
                              ?.map((n) => n.phone)
                              .filter((phone) => phone && phone.trim() !== '' && phone !== '+') || []

                          if (validationTimeoutRef.current) {
                            clearTimeout(validationTimeoutRef.current)
                          }

                          validationTimeoutRef.current = setTimeout(() => {
                            setPhoneNumbersToValidate(phoneNumbers)
                          }, 600)
                        }, 0)
                      }}
                      onEnterKeyPress={onSubmit(handleSubmit)}
                      size="small"
                      mode={isSuggested(props.field.value?.phone) ? 'suggestion' : 'default'}
                    />
                  )}
                />
                <Icon
                  name="removeEntry"
                  className={classes.btnRemoveRecipient}
                  onClick={() => handleRemovePhone(index)}
                />
              </div>
            ))}
          </div>
          <button type="button" className={classes.btnAddRecipient} onClick={handleAddPhone}>
            Add another recipient
          </button>

          {hasFocus && hasErrors() && showErrors && (
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gridGap={5}>
              <div className={classes.errorIcon}>
                <Icon name="eventCardError" />
              </div>
              <span className={classes.error}>{getErrorMessage()}</span>
            </Box>
          )}

          {hasFocus && event.errors.length > 0 && !event.active && (
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gridGap={5}>
              <div className={classes.errorIcon}>
                <Icon name="eventCardError" />
              </div>
              <span className={classes.error}>{event.errors.join(', ')}</span>
            </Box>
          )}

          {hasFocus && !remindersPausedReason && (
            <Button type="submit" disabled={hasErrors()} className={classes.btnSave}>
              Save and activate
            </Button>
          )}

          {hasFocus && remindersPausedReason && (
            <EventCardPausedActions
              reason={remindersPausedReason}
              freeRemindersLimit={profile.effective_free_reminders_limit}
              saveDisabled={hasErrors()}
            />
          )}

          {hasFocus && standingConsentCandidates.length > 0 && (
            <EventCardBulkAutoActivateSwitch
              standingConsentCandidates={standingConsentCandidates}
              bulkAutoActivate={bulkAutoActivate}
              bulkAutoActivateTouched={bulkAutoActivateTouched}
              standingConsentHints={standingConsentHints}
              onChange={(checked) => {
                setBulkAutoActivateTouched(true)
                setBulkAutoActivate(checked)
              }}
            />
          )}
        </form>
      </div>

      {reviewTimingIssuesRef.current === 'open' && (
        <ReviewTimingIssuesModal
          confirmationEnabled={event.confirmation_enabled}
          confirmationOffsets={event.confirmation_offsets}
          reminderEnabled={event.reminder_enabled}
          reminderOffsets={event.reminder_offsets}
          eventDate={eventDate}
          onClose={() => {
            reviewTimingIssuesRef.current = 'closed'
            forceUpdate({}) // Force a re-render to close the modal
          }}
          onBack={() => {
            reviewTimingIssuesRef.current = 'closed'
            forceUpdate({}) // Force a re-render to close the modal
          }}
          onSend={() => {
            reviewTimingIssuesRef.current = 'send'
            onSubmit(handleSubmit)()
          }}
        />
      )}
    </>
  )
}
